import React, { useEffect, useState } from "react";
import api from "../lib/auth";
import ReportingGroupEditor from "./ReportingGroupEditor";
import {
  Layers, CalendarClock, FileText, Mail,
  History, ShieldCheck, Eye, Settings2
} from "lucide-react";

const SECTIONS = [
  ["officer", "Officer details and licence"],
  ["shift", "Shift start, finish and duration"],
  ["signatures", "Sign-on and sign-off signatures"],
  ["patrols", "Patrol and checkpoint activity"],
  ["exceptions", "Missed patrols and non-compliance"],
  ["incidents", "Incidents and actions taken"],
  ["noise", "Noise complaints and guest/site details"],
  ["photos", "Photographs and attachments"],
  ["followup", "Outstanding management follow-up"],
  ["gps", "GPS and QR verification"],
  ["handover", "Handover notes"],
  ["checklists", "Mobile checklists"],
  ["regulatory", "Regulatory log entries"],
  ["summary", "Management summary"],
];

const TABS = [
  ["sites", "Sites & Groups", Layers],
  ["schedule", "Scheduling", CalendarClock],
  ["builder", "Report Builder", FileText],
  ["recipients", "Recipients", Mail],
  ["preview", "Preview & History", History],
];

const fieldClass =
  "w-full rounded-lg border border-zinc-700 bg-zinc-900 " +
  "px-3 py-2 text-sm text-white focus:outline-none " +
  "focus:border-blue-500";

export default function ReportingAdministrationPanel({ groups = [], onGroupsChanged }) {
  const [tab, setTab] = useState("sites");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsVersion, setSettingsVersion] = useState("");
  const [savedSettings, setSavedSettings] = useState(null);
  const [loadedGroupId, setLoadedGroupId] = useState("");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState("");
  const [scope, setScope] = useState("group");
  const [frequency, setFrequency] = useState("daily");
  const [cutoff, setCutoff] = useState("07:00");
  const [deliveryTime, setDeliveryTime] = useState("08:00");
  const [sections, setSections] = useState(
    SECTIONS.map(([key]) => key)
  );
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");

  useEffect(() => {
    if (!groups.some((group) => group.id === selectedGroupId)) {
      setSelectedGroupId("");
    }
  }, [groups, selectedGroupId]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedGroupId) {
      setSavedSettings(null);
      setLoadedGroupId("");
      setSettingsVersion("");
      setSettingsError("");
      setSettingsSuccess("");
      setSettingsLoading(false);
      return;
    }

    const loadSettings = async () => {
      setSettingsLoading(true);
      setSettingsError("");
      setSettingsSuccess("");
      setSavedSettings(null);
      setLoadedGroupId("");
      setSettingsVersion("");

      try {
        const response = await api.get(
          `/reporting-groups/${encodeURIComponent(selectedGroupId)}/settings`
        );

        if (cancelled) return;

        const data = response.data;
        const settings = data.settings;

        setSavedSettings(settings);
        setLoadedGroupId(selectedGroupId);
        setSettingsVersion(data.version);
        setFrequency(settings.frequency);
        setCutoff(settings.report_cutoff_time);
        setDeliveryTime(settings.send_time);
        setSections(settings.report_sections);
        setTo((settings.to_emails || []).join(", "));
        setCc((settings.cc_emails || []).join(", "));
        setBcc((settings.bcc_emails || []).join(", "));
      } catch (error) {
        if (!cancelled) {
          setSavedSettings(null);
          setLoadedGroupId("");
          setSettingsVersion("");
          setSettingsError(
            error?.response?.data?.detail ||
            "Unable to load reporting configuration."
          );
        }
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    };

    loadSettings();

    return () => {
      cancelled = true;
    };
  }, [selectedGroupId]);

  const parseRecipients = (value) =>
    value.split(",").map((address) => address.trim()).filter(Boolean);

  const saveSettings = async () => {
    if (
      !selectedGroupId ||
      loadedGroupId !== selectedGroupId ||
      !savedSettings ||
      !settingsVersion ||
      settingsLoading ||
      settingsSaving
    ) return;

    setSettingsError("");
    setSettingsSuccess("");

    const toEmails = parseRecipients(to);
    const ccEmails = parseRecipients(cc);
    const bccEmails = parseRecipients(bcc);
    const allEmails = [...toEmails, ...ccEmails, ...bccEmails];

    const emailPattern = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;

    if (allEmails.some((address) => !emailPattern.test(address))) {
      setSettingsError("Please correct invalid recipient email addresses.");
      return;
    }

    if (
      new Set(allEmails.map((address) => address.toLowerCase())).size !==
      allEmails.length
    ) {
      setSettingsError("A recipient cannot appear more than once.");
      return;
    }

    if (sections.length === 0) {
      setSettingsError("Select at least one PDF report section.");
      return;
    }

    const payload = {
      ...savedSettings,
      enabled: false,
      frequency: "daily",
      report_cutoff_time: "07:00",
      send_time: "08:00",
      timezone: "Australia/Sydney",
      require_finalized: true,
      include_patrols: true,
      include_incidents: false,
      report_sections: sections,
      to_emails: toEmails,
      cc_emails: ccEmails,
      bcc_emails: bccEmails,
    };

    const savingGroupId = selectedGroupId;
    setSettingsSaving(true);

    try {
      const response = await api.put(
        `/reporting-groups/${encodeURIComponent(savingGroupId)}/settings`,
        payload,
        { headers: { "If-Match": settingsVersion } }
      );

      const updated = response.data;
      setSavedSettings(updated.report_settings);
      setSettingsVersion("");
      setSettingsSuccess(
        "Configuration saved. Reload the group to make further changes."
      );
      if (onGroupsChanged) await onGroupsChanged();
    } catch (error) {
      setSettingsVersion("");
      const status = error?.response?.status;
      setSettingsError(
        status === 409
          ? "Settings changed elsewhere. Select another group and return to reload before saving."
          : status === 422
          ? "Configuration rejected. Check the selected options and recipients."
          : "Unable to save reporting configuration."
      );
    } finally {
      setSettingsSaving(false);
    }
  };

  const toggleSection = (key) => {
    setSections((previous) =>
      previous.includes(key)
        ? previous.filter((item) => item !== key)
        : [...previous, key]
    );
  };

  const exportDraft = () => {
    const draft = {
      scope,
      frequency,
      timezone: "Australia/Sydney",
      cutoff,
      delivery_time: deliveryTime,
      report_sections: sections,
      recipients: { to, cc, bcc },
      automatic_external_delivery: false,
      status: "preview_only",
    };

    const blob = new Blob(
      [JSON.stringify(draft, null, 2)],
      { type: "application/json" }
    );

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "rapidx-reporting-draft.json";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-blue-400" />
            Reporting Administration
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Configure site, group and client reporting in one place.
          </p>
        </div>
        <span className="text-xs rounded-full border border-amber-700
                         px-3 py-1 text-amber-300">
          Group management & configuration preview
        </span>
      </div>

      <div className="rounded-lg border border-zinc-700 p-4 space-y-3">
        <label className="block text-sm font-medium">
          Configure saved reporting group
        </label>
        <select
          className={fieldClass}
          value={selectedGroupId}
          disabled={settingsSaving}
          onChange={(event) => setSelectedGroupId(event.target.value)}
        >
          <option value="">Select a reporting group</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name} ({group.site_ids?.length || 0} locations)
            </option>
          ))}
        </select>
        {settingsLoading && (
          <p className="text-sm text-zinc-400">
            Loading saved reporting configuration...
          </p>
        )}
        {settingsError && (
          <p role="alert" className="text-sm text-red-400">
            {settingsError}
          </p>
        )}
        {savedSettings && (
          <p className="text-sm text-emerald-400">
            Saved configuration loaded. Automatic delivery remains disabled.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-zinc-800 pb-3">
        {TABS.map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={
              "flex items-center gap-2 rounded-lg px-3 py-2 text-sm " +
              (tab === key
                ? "bg-blue-600 text-white"
                : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700")
            }
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-zinc-800 bg-[#18181b] p-5">
        {tab === "sites" && (
          <div className="space-y-4">
            <h3 className="font-semibold">Reporting scope</h3>
            <p className="text-sm text-zinc-400">
              Select how locations will be organised for reporting.
            </p>
            <select
              className={fieldClass}
              value={scope}
              onChange={(event) => setScope(event.target.value)}
            >
              <option value="site">Individual site</option>
              <option value="group">Location group / patrol run</option>
              <option value="client">Entire client portfolio</option>
            </select>

            {scope === "group" ? (
              <ReportingGroupEditor
                groups={groups}
                onSaved={onGroupsChanged}
              />
            ) : (
              <div className="rounded-lg border border-zinc-700 p-4">
                <p className="text-sm font-medium">
                  {scope === "site"
                    ? "Individual site reporting"
                    : "Client portfolio reporting"}
                </p>
                <p className="text-sm text-zinc-400 mt-2">
                  Configuration for this reporting scope is not yet
                  connected to persistent backend settings.
                  Select Location group / patrol run to create
                  a reporting group now.
                </p>
              </div>
            )}

            <p className="text-xs text-zinc-400">
              {groups.length} saved reporting groups.
              Existing groups are listed below this administration panel.
            </p>

            <p className="flex items-center gap-2 text-sm text-emerald-400">
              <ShieldCheck className="w-4 h-4" />
              Client-facing reports require verified site ownership.
            </p>
          </div>
        )}

        {tab === "schedule" && (
          <div className="space-y-4">
            <h3 className="font-semibold">Reporting schedule</h3>
            <label className="block text-sm text-zinc-300">
              Reporting frequency
            </label>
            <select
              className={fieldClass}
              value={frequency}
              onChange={(event) => setFrequency(event.target.value)}
            >
              <option value="daily">Daily scheduled report</option>
              <option value="shift" disabled>
                End-of-shift (not yet available)
              </option>
              <option value="both" disabled>
                Daily and end-of-shift (not yet available)
              </option>
            </select>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-sm">
                Daily reporting cutoff
                <input
                  type="time"
                  value={cutoff}
                  onChange={(event) => setCutoff(event.target.value)}
                  disabled
                  className={fieldClass + " mt-2"}
                />
              </label>
              <label className="text-sm">
                Target daily delivery time
                <input
                  type="time"
                  value={deliveryTime}
                  onChange={(event) => setDeliveryTime(event.target.value)}
                  disabled
                  className={fieldClass + " mt-2"}
                />
              </label>
            </div>

            <p className="text-xs text-zinc-400">
              Timezone: Australia/Sydney. End-of-shift reporting
              does not require independent supervisor approval.
              Daily approval rules remain separate.
            </p>
          </div>
        )}

        {tab === "builder" && (
          <div className="space-y-4">
            <h3 className="font-semibold">PDF report contents</h3>
            <p className="text-sm text-zinc-400">
              Select which sections should appear in the report.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SECTIONS.map(([key, label]) => (
                <label
                  key={key}
                  className="flex items-start gap-3 rounded-lg
                             border border-zinc-800 p-3 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={sections.includes(key)}
                    onChange={() => toggleSection(key)}
                    className="mt-1"
                  />
                  {label}
                </label>
              ))}
            </div>
            <p className="text-xs text-zinc-400">
              {sections.length} report sections selected.
              Sensitive content remains subject to client permissions.
            </p>
          </div>
        )}

        {tab === "recipients" && (
          <div className="space-y-4">
            <h3 className="font-semibold">Email distribution</h3>
            {[
              ["To", to, setTo],
              ["Cc", cc, setCc],
              ["Bcc", bcc, setBcc],
            ].map(([label, value, setter]) => (
              <label key={label} className="block text-sm">
                {label} recipients
                <input
                  type="text"
                  value={value}
                  onChange={(event) => setter(event.target.value)}
                  placeholder="Separate multiple addresses with commas"
                  className={fieldClass + " mt-2"}
                />
              </label>
            ))}

            <div className="rounded-lg border border-amber-800
                            bg-amber-950/20 p-4 text-sm text-amber-300">
              Automatic external delivery is disabled.
              This interface cannot activate email transmission.
            </div>
          </div>
        )}

        {tab === "preview" && (
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Configuration preview
            </h3>
            <div className="rounded-lg bg-zinc-950 p-4 text-sm space-y-2">
              <p>Scope: {scope}</p>
              <p>Frequency: {frequency}</p>
              <p>Daily cutoff: {cutoff}</p>
              <p>Target delivery: {deliveryTime}</p>
              <p>Selected PDF sections: {sections.length}</p>
              <p>External delivery: Disabled</p>
            </div>
            <p className="text-xs text-zinc-400">
              PDF rendering and delivery history will be connected
              to the reporting backend in a later stage.
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-2">
          <p className="text-xs text-zinc-500">
            Saved group configurations are retained in RapidX.
            Automatic client delivery remains disabled.
          </p>
          {settingsSuccess && (
            <p role="status" className="text-sm text-emerald-400">
              {settingsSuccess}
            </p>
          )}
          <button
            type="button"
            onClick={saveSettings}
            disabled={
              !selectedGroupId ||
              loadedGroupId !== selectedGroupId ||
              !savedSettings ||
              !settingsVersion ||
              settingsLoading ||
              settingsSaving
            }
            className="rounded-lg bg-emerald-700 hover:bg-emerald-600
                       disabled:opacity-40 disabled:cursor-not-allowed
                       px-4 py-2 text-sm font-medium"
          >
            {settingsSaving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
        <button
          type="button"
          onClick={exportDraft}
          className="rounded-lg bg-blue-600 hover:bg-blue-500
                     px-4 py-2 text-sm font-medium"
        >
          Export Configuration Draft
        </button>
      </div>
    </div>
  );
}
